<template>
  <ViewContainer :title="$t('tasks.task_other')" class="mt-n1 pt-4">
    <template v-slot:append>
      <div class="search-container" data-tourid="tasks-input-search">
        <SearchInput
          class="white task-search-bar"
          :value="searchTerms"
          @change="handleSearchTerm"
        />
      </div>
    </template>

    <v-card class="pa-0 ma-0">
      <TaskFilters @groupChange="groupBy = $event" class="py-6 px-8" />
      <v-divider />
      <div v-if="!isKanbanView" class="px-8 mt-4">
        <InlineCreator
          showDetailFields
          :shouldAutoAssign="shouldAutoAssign"
          @addDetails="handleAddDetails"
          @taskWasCreated="handleTaskWasCreated"
        />
      </div>

      <div class="px-8 pb-4 mt-4" style="overflow-x: scroll;">
        <div v-if="isKanbanView" style="height: calc(67vh + 4px)">
          <TasksKanbanView />
        </div>

        <div v-else style="overflow: scroll; height: 60vh">
          <TaskGroup
            v-for="(group, index) in activeGroup"
            class="mb-4"
            ref="taskGroup"
            :key="index"
            :groupKey="group.key"
            :title="group.title"
            :groupFilters="group.groupFilters"
            :initialLimit="group.initialLimit"
          />
        </div>
      </div>
    </v-card>
  </ViewContainer>
</template>
<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import SearchInput from '@/components/Core/SearchInput.vue'

import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import TaskGroup from './Header/TaskGroup.vue'
import InlineCreator from './Header/InlineCreator.vue'
import TaskFilters from './Header/TaskFilters.vue'
import TasksKanbanView from './Kanban/Kanban.vue'

const GROUP_OPTIONS = {
  dueDate: 'dueDate',
  priority: 'priority',
  all: 'all',
}

export default {
  name: 'TasksView',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    TaskGroup,
    InlineCreator,
    TaskFilters,
    SearchInput,
    TasksKanbanView,
  },

  data() {
    return {
      groupBy: this.$route.query.group || GROUP_OPTIONS.all,
      showTaskCreator: false,
      viewType:
        this.$route.query.viewType || this.$constants.TASKS_VIEW_OPTIONS.list,
    }
  },

  async created() {
    eventHub.$on(
      HUB_EVENTS.TASK_CREATION_DRAWER.CREATED,
      this.handleTaskWasCreated
    )
    eventHub.$on(
      HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED,
      this.handleTaskWasUpdated
    )

    this.checkInitialURL()
  },

  computed: {
    ...mapGetters(['accountId']),

    isKanbanView() {
      return this.viewType === this.$constants.TASKS_VIEW_OPTIONS.kanban
    },

    pastDueDate() {
      const thisWeek = utils.dates.getDateRangeByFilter(
        this.$constants.DATE_FILTER_OPTIONS.currentWeek
      )
      return { to: thisWeek.startDate }
    },

    thisWeekDueDate() {
      const lastWeek = utils.dates.getDateRangeByFilter(
        this.$constants.DATE_FILTER_OPTIONS.lastWeek
      )
      const nextWeek = utils.dates.getDateRangeByFilter(
        this.$constants.DATE_FILTER_OPTIONS.nextWeek
      )

      return { from: lastWeek.endDate, to: nextWeek.startDate }
    },

    laterDueDate() {
      const thisWeek = utils.dates.getDateRangeByFilter(
        this.$constants.DATE_FILTER_OPTIONS.currentWeek
      )
      return { from: thisWeek.endDate }
    },

    dueDateGroups() {
      return [
        {
          key: 'past_due',
          title: this.$t('tasks.past_due'),
          groupFilters: { dueDate: this.pastDueDate },
          initialLimit: 7,
        },
        {
          key: 'this_week',
          title: this.$t('tasks.this_week'),
          groupFilters: {
            dueDate: this.thisWeekDueDate,
          },
          initialLimit: 7,
        },
        {
          key: 'later',
          title: this.$t('tasks.later'),
          groupFilters: { dueDate: this.laterDueDate },
          initialLimit: 7,
        },
        {
          key: 'no_due_date',
          title: this.$t('tasks.no_due_date'),
          groupFilters: { hasDueDate: false },
          initialLimit: 7,
        },
      ]
    },

    priorityGroups() {
      return [
        {
          key: 'high',
          title: this.$t('rev.priority.high'),
          groupFilters: { priority: this.$constants.priorities.high },
          initialLimit: 7,
        },
        {
          key: 'medium',
          title: this.$t('rev.priority.medium'),
          groupFilters: { priority: this.$constants.priorities.medium },
          initialLimit: 7,
        },
        {
          key: 'low',
          title: this.$t('rev.priority.low'),
          groupFilters: { priority: this.$constants.priorities.low },
          initialLimit: 7,
        },
        {
          key: 'no_priority',
          title: this.$t('rev.priority.none'),
          groupFilters: { hasPriority: false },
          initialLimit: 7,
        },
      ]
    },

    allTasks() {
      return [
        {
          key: 'all_tasks',
          title: this.$t('tasks.all'),
          groupFilters: {},
          initialLimit: 20,
        },
      ]
    },

    activeGroup() {
      if (this.groupBy === GROUP_OPTIONS.priority) return this.priorityGroups
      if (this.groupBy === GROUP_OPTIONS.dueDate) return this.dueDateGroups
      return this.allTasks
    },

    shouldAutoAssign() {
      return Boolean(this.$route.query.type === 'assignedToMe')
    },

    searchTerms() {
      return this.$route.query.searchTerms || ''
    },
  },

  methods: {
    handleTaskWasCreated() {
      this.$refs.taskGroup?.forEach(taskGroup => taskGroup.refreshList())
      this.showSuccess(this.$t('tasks.created_successfully'))
    },

    handleTaskWasUpdated(groupKey) {
      this.$refs.taskGroup?.forEach(taskGroup => {
        if (!groupKey) return taskGroup.refreshList()
        if (groupKey === taskGroup.groupKey) return taskGroup.refreshList()
      })
    },

    handleSearchTerm: _.debounce(function(searchTerms) {
      this.replaceRoute({
        path: this.$route.path,
        query: { ...this.$route.query, searchTerms },
      })
      this.trackTasks('tasks.filter', {
        sort: this.$route.query.sort,
        groupBy: this.$route.query.group,
        status:
          _.isArray(this.$route.query.status) || !this.$route.query.status
            ? this.$route.query.status
            : [this.$route.query.status],
        type: this.$route.query.type,
        hasTerms: Boolean(this.$route.query.searchTerms),
        hasAssignee: !_.isEmpty(this.$route.query.assignees),
      })
    }, 500),

    handleAddDetails(task) {
      eventHub.$emit(
        HUB_EVENTS.TASK_CREATION_DRAWER.CREATE,
        {
          ...task,
          isPrivate: true,
        },
        'tasks'
      )
    },

    checkInitialURL() {
      if (!this.$route.query?.task) return
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE)
    },
  },

  watch: {
    '$route.query'() {
      if (!this.$route.query?.task) return
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE)
    },
    '$route.query.viewType'(newVal) {
      this.viewType = newVal
    },
  },

  beforeDestroy() {
    eventHub.$off(HUB_EVENTS.TASK_CREATION_DRAWER.CREATED)
    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED)
  },
}
</script>

<style lang="scss" scoped>
.search-container {
  width: 400px;
}
</style>

<style lang="scss">
.search-container .task-search-bar .v-input__control .v-input__slot fieldset {
  height: auto !important;
}
</style>
